import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Info, X } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import { handleModalEscape, pushModalEscape } from '../lib/modal-escape';
import { useNarrow } from '../lib/use-narrow';
import { RailCatcher } from './RailCatcher';
import { IconButton, ScrollArea, TextButton } from './symphony/ui';
import paneScrim from './pane-scrim.module.css';
import styles from './Modal.module.css';

/** Keeps the rail inert without letting a press there dismiss, when dismissal is off. */
const noop = () => {};

/**
 * The width at or under which a `fullScreenOnSmall` modal fills the viewport instead of floating a card
 * in it. **Mirrored by the `@media (max-width: 700px)` block in Modal.module.css** — the geometry lives
 * in CSS (see that file's header) and only the chrome SWAP needs the number in JS, so the two must move
 * together.
 */
export const MODAL_FULL_SCREEN_MAX_PX = 700;

/**
 * Is a `fullScreenOnSmall` modal currently filling the viewport?
 *
 * Exported because a CALLER may need to vary what it puts in the header between the two presentations —
 * the doc viewer shows three labelled buttons on a desktop and a discuss bubble + kebab on a phone
 * (Figma `15897:424580`), which is a different control set, not a narrower row of the same one. Such a
 * caller must ask the SAME question the shell asks: a `useNarrow(720)` of its own would swap the controls
 * at one width while the shell reshapes the header at another, and the header would spend 20px of width
 * drawn as a phone presenter holding desktop buttons.
 */
export function useModalFullScreen(): boolean {
  return useNarrow(MODAL_FULL_SCREEN_MAX_PX + 1);
}

// The DS modal widths (Figma "Modal / Drawer" → Custom modal layout, node 1136:1700).
const SIZE_WIDTH: Record<ModalSize, number> = {
  minimum: 512,
  small: 672,
  medium: 768,
  maximum: 1280,
};

export type ModalSize = 'minimum' | 'small' | 'medium' | 'maximum';

// The one modal shell (the Figma "Modal / Drawer" DS component, node 1132:1576): a scrim over a
// centered card with a top-inline-end close (✕), an optional header (title + help icon + description),
// a content slot, an optional right-aligned footer for the action buttons, and an optional grey
// footnote strip. Token-driven, so it takes on whichever brand's palette is active. Portaled to
// <body> to escape any transformed or overflow-clipped ancestor. Closes on Escape and on a scrim
// click; the footer buttons are supplied by the caller so each modal keeps its own actions.
export function Modal({
  open,
  onClose,
  title,
  titleInfo,
  description,
  headerActions,
  backAction,
  children,
  footer,
  footnote,
  headerSeparator = false,
  footerSeparator = false,
  size,
  width = 440,
  maxHeightPx,
  labelledBy,
  cardClassName,
  cardTheme,
  contentClassName,
  fullScreenOnSmall = true,
  scrollBody = false,
  dismissOnScrimClick = true,
  hideClose = false,
  presentation = 'centered',
  besideChat = false,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  // The DS header's optional help/info circle. Pass the tooltip text; renders an 18px info glyph.
  titleInfo?: string;
  // The DS header's secondary line under the title (14/18, secondary ink).
  description?: ReactNode;
  /**
   * The header's TRAILING cluster — the overlay's own controls, sitting on the same row as the title
   * block rather than down in the footer (Figma `15652:57057`, the doc viewer). Pass the buttons; the
   * shell supplies the cluster itself (`display: flex`, gap 8, `flex-shrink: 0`, centred), reserves the
   * 48 of inline-end clearance the ✕ needs beside them, and grows the ✕ to the 36px box that lines up
   * with a row of 36px controls.
   *
   * It is a SHELL slot, not viewer-local markup, because "the actions belong beside the title, not under
   * the content" is a property of the overlay chrome — the pack/app drill-ins want the same row, and a
   * second hand-rolled header inside one caller's content slot is exactly the drift orion-web/CLAUDE.md
   * warns about.
   *
   * The footer slot is unaffected: a modal may use both (the viewer keeps its Discuss primary down there).
   * Below 700px, where the card fills the viewport and the ✕ becomes a leading "‹ back", the cluster
   * stacks UNDER the title block instead of beside it — three labelled buttons cannot share a phone's
   * header row with a file name without crushing one of them.
   */
  headerActions?: ReactNode;
  /**
   * A labelled way back to the drill-in this one was opened FROM — "‹ Back to Launch the book club".
   *
   * It is a shell slot rather than caller markup because the rule it expresses belongs to the chrome:
   * a stacked drill-in must say where it returns to, in the same place, on every modal that stacks.
   * The label NAMES the destination; a bare arrow leaves the reader guessing whether it dismisses.
   *
   * It is deliberately NOT the shell's own close control (the ✕, or the leading "‹" the full-screen
   * presentation draws). Closing means closing: a reader who wants out of a two-deep stack presses ✕
   * once, and a reader who wants the goal back presses the labelled row. Folding the two together is
   * the opposite complaint one level down.
   *
   * Pass it only when there genuinely IS something underneath — see `TaskSidePanelContext`, where
   * `back` is undefined at stack depth 1, so a task opened from the Tasks list never grows one.
   */
  backAction?: { label: string; onBack: () => void };
  children?: ReactNode;
  footer?: ReactNode;
  // The DS grey small-print strip at the very bottom of the card (legal notes, hints, help links).
  footnote?: ReactNode;
  // The DS hairline between header and content. Off by default so the existing dialogs keep their
  // plain look; the feature modals built to the DS opt in.
  headerSeparator?: boolean;
  // The DS hairline between content and footer.
  footerSeparator?: boolean;
  // The DS width tokens (Minimum 512 / Small 672 / Medium 768 / Maximum 1280). Overrides `width`.
  size?: ModalSize;
  width?: number;
  // Cap the card at this pixel height (never past the viewport), so the body scrolls inside a fixed-height
  // modal rather than the card growing with its content. Pair with `scrollBody`. Overrides the plain
  // `.cardScroll` viewport cap, and yields to the full-screen-on-small variant (it is a class, not an
  // inline style — see `.cardCap`). **Design QA asks EVERY centered modal to sit at 650** (Monday
  // 12771405229) — pass `maxHeightPx={650} scrollBody`; test/modal-max-height.test.ts holds the roster
  // and the reason for each modal that is deliberately not on it.
  maxHeightPx?: number;
  labelledBy?: string;
  // Below 700px, fill the viewport instead of floating a card in it: edge-to-edge scrim, no radius,
  // no shadow, and the card scrolls. This is the DEFAULT on mobile — a floating card in a phone
  // viewport reads as an unfinished sheet, and native presents these same dialogs full-screen, so
  // this is the parity behaviour. A dialog that genuinely wants to stay a centered card on a phone
  // opts out with `fullScreenOnSmall={false}`.
  fullScreenOnSmall?: boolean;
  // Cap the card at the viewport height and scroll the BODY (not the whole card) inside a ScrollArea,
  // so the header + footer stay pinned while long content scrolls. Opt-in — a modal whose content owns
  // an overflowing child (an absolutely-positioned dropdown, e.g. the phone country picker) must NOT
  // set it, since a scroll container would clip that child.
  scrollBody?: boolean;
  // Whether a press outside the card dismisses it. On by default, because for most dialogs clicking
  // away IS the cheap way out. A flow that would lose real progress turns it off — a code already
  // sent to a phone cannot be un-sent, and a stray click beside the card should not cost it. Escape
  // still closes either way: it is a deliberate keypress, and the expected dismissal for a dialog.
  dismissOnScrimClick?: boolean;
  // Suppress the shell's own way out — both the desktop ✕ AND the full-screen "‹ back". For a
  // full-screen sheet whose OWN chrome already draws a back control (SkillsModalMobile) — a second one
  // would just be doubled. Escape and the scrim still close, so the modal is never trapped.
  hideClose?: boolean;
  // Optional class on the dialog card. Lets a caller re-assert its own palette tokens
  // (--surface-base-rgb / --ink-rgb / --sym-*) — the shell portals to <body>, so a modal that
  // must NOT inherit the active brand's body palette (e.g. the always-light package picker)
  // scopes the right tokens here rather than the shell hardcoding a look.
  cardClassName?: string;
  /**
   * Pins the CARD to one palette whatever the reader's theme — the same device the mobile drawer uses
   * (`data-theme="dark"` on its own root): every `--ds-*` inside resolves from that palette, so a
   * permanently-dark card is token-pure rather than a set of literals. For a card the design draws dark
   * on a light page; everything else leaves it unset and follows the theme.
   */
  cardTheme?: 'dark' | 'light';
  // Optional class on the content slot, ADDED to `.content`/`.contentBare` rather than replacing
  // them. `.content` is a plain block box — a caller whose child relies on flex-shrink (e.g. a
  // fixed-height reading pane that must reclaim space on a short viewport) needs this to turn the
  // slot itself into a flex column with `min-height: 0`, or the child's own flex properties are
  // inert (its parent isn't a flex container) and a short viewport clips the footer instead of
  // shrinking the content. Scoped per-caller rather than added to the shared class because most of
  // the ~20 existing dialogs pass plain block children that rely on normal margin flow.
  contentClassName?: string;
  /**
   * WHERE the card sits. `centered` (default) is the DS "Modal / Drawer": a floating card in the middle
   * of the pane, which on a small viewport fills it (`fullScreenOnSmall`).
   *
   * `sheet` is the phone's BOTTOM SHEET (Figma `15910:425995` "Card/Medium"): full width, docked to the
   * bottom edge, rounded on its TOP corners only, sized to its content, with the grab handle above the
   * content. It is a third presentation, not a change to the full-screen one — a menu of row actions is
   * not a screen, so filling the viewport with it would read as a navigation the user did not ask for.
   *
   * It lives here rather than in a sheet primitive of its own because everything below the surface is
   * identical — the pane-scoped scrim, the shared Escape layer (`lib/modal-escape.ts`), the rail-inert
   * catcher, the portal — and a second copy of that is exactly the drift orion-web/CLAUDE.md warns
   * about. `fullScreenOnSmall` is forced OFF for a sheet: the two are contradictory presentations, and
   * a caller cannot get that wrong.
   */
  presentation?: 'centered' | 'sheet';
  /**
   * Leave the right-docked chat OUTSIDE the scrim, so the dialog floats over the content pane while the
   * conversation stays visible and usable beside it — for an editor the assistant can help with mid-edit
   * (the profile-link Edit dialog). The edge reads `--docked-chat-w`, which AppFrame mirrors onto `:root` for a
   * dialog portaled to <body>. With no docked chat it is 0 and this is the ordinary pane scrim.
   */
  besideChat?: boolean;
}) {
  const { t } = useTranslation();
  // Match the 700px breakpoint the full-screen CSS below triggers at (NOT useNarrow's 720 default), so the
  // back button appears exactly when the card fills the viewport. Through the exported hook, so a caller
  // that varies its own header controls by presentation reads the same boolean — see `useModalFullScreen`.
  const fullScreen = useModalFullScreen();
  // Read through a ref so a fresh inline `onClose` doesn't re-register (and reorder) the escape stack.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) {
      return;
    }
    // Escape closes THIS modal and nothing else — see lib/modal-escape.ts for why it must be captured
    // and swallowed rather than shared with the panels listening underneath.
    //
    // Known latent limitation, no call site hits it today: the stack's "innermost" is really "registered
    // last", and React runs effects CHILD-BEFORE-PARENT. So two Modals mounting in the SAME commit (a
    // modal that renders another already-open modal in its own subtree) would push the CHILD first and the
    // OUTER one last, leaving the outer on top of the stack — one Escape would close the outer and strand
    // the inner. Every current nesting opens the second modal in a LATER commit (a click), where the order
    // is correct. If a same-commit pair ever appears, the fix is an explicit depth/ordinal on the stack
    // entry rather than relying on registration order.
    const pop = pushModalEscape(() => onCloseRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const sheet = presentation === 'sheet';
  // A sheet is never the full-screen card: they are contradictory presentations of the same box.
  const fillsSmall = fullScreenOnSmall && !sheet;
  // `headerActions` counts: a header that is nothing but its trailing controls is still a header, and
  // leaving it out here would silently drop the actions of any caller that passes no title.
  const hasHeader = Boolean(title || description || titleInfo || headerActions);
  const modalWidth = size ? SIZE_WIDTH[size] : width;

  const contentClasses = [
    hasHeader ? styles.content : styles.contentBare,
    hasHeader && headerSeparator ? styles.contentUnderSeparator : undefined,
    sheet ? styles.sheetContent : undefined,
    contentClassName,
  ].filter(Boolean) as string[];

  // The DS draws its header/footer rules inside a 20-of-clearance block on BOTH sides (Figma
  // 12889-206300: the header container is py-20 with the rule under it, the footer's button row py-20
  // under its own rule). The shell's plain 16 left the two hairlines 4 short, so the rule is nudged to
  // 20 only for the dialogs that draw it — a rule-less header/footer keeps its 16.
  const headerClasses = [
    styles.header,
    headerSeparator ? styles.headerAboveSeparator : undefined,
    // Reserves the ✕'s 48 of inline-end clearance and un-reserves the title row's own 24 (the actions
    // cluster now stands between the title and that corner). See `.headerHasActions`.
    headerActions ? styles.headerHasActions : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const footerClasses = [styles.footer, footerSeparator ? styles.footerUnderSeparator : undefined]
    .filter(Boolean)
    .join(' ');

  /**
   * The PHONE PRESENTER header — Figma `15897:424580`, the mobile file presenter:
   * `[circular ✕] [title / subtitle] ……… [trailing controls]`, on one row, inset 20, above a full-bleed
   * rule. A full-screen card whose header carries its own trailing cluster takes this shape.
   *
   * Gated on `headerActions` for the same reason `.headerHasActions` / `.closeWithActions` are: the
   * cluster IS what makes this the DS's action header, and reshaping the ~20 plain full-screen dialogs
   * (or TaskDetailMobile / SkillsModalMobile) around one node nobody drew them from would be a change to
   * every phone screen in the app. The geometry is CSS (see the module's `@media` block); only the
   * ✕-vs-"‹ back" swap and the card's own page background need the boolean.
   */
  const presenterHeader = fillsSmall && fullScreen && hasHeader && Boolean(headerActions);

  // Full-screen on mobile presents like a pushed native screen, so the way out is a leading "‹ back"
  // (top-inline-start) rather than the floating ✕ — the same chrome the native full-screen sheets and
  // TaskDetailMobile/SkillsModalMobile carry. `hideClose` still wins: a modal that draws its own back
  // control (SkillsModalMobile) opts out of both so it isn't doubled.
  //
  // The PRESENTER is the one exception, and it is a deliberate one: node `15897:424580` draws a circular
  // ✕ as the header row's leading control, both phones ship exactly that (iOS/Android
  // `DocumentViewerChrome`: a `Radius.pill` 42 box with a 16 glyph), and the viewer is the one screen
  // where all three surfaces sit side by side. A floating "‹ back" also could not be vertically centred
  // against a two-line title stack the way the node centres it. So the back control yields there.
  const fullScreenBack = fillsSmall && fullScreen && !hideClose && !presenterHeader;
  const showClose = !hideClose && !fullScreenBack;

  // ONE ✕, rendered in one of two places: the header ROW on the presenter (where it is a real flex item
  // with real gaps beside it), and the card's top-inline-end corner everywhere else, where it is
  // absolutely positioned against the card and so unaffected by which element it is nested in.
  const closeButton = showClose ? (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('common.close')}
      // The 36px box is scoped to the actions case rather than made the shell default: growing every
      // dialog's ✕ by 4px and moving it 2px is a change to ~20 screens nobody asked for, while a ✕
      // that sits 4px smaller than the 36px controls beside it reads as a mistake in the one header
      // that has them.
      className={['orion-modal-close', styles.close, headerActions ? styles.closeWithActions : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      <X size={16} aria-hidden />
    </button>
  ) : null;

  // The scrim is scoped to the CONTENT PANE so the sidebar/nav stays crisp (per the Figma — only the
  // content behind the card blurs, not the rail); `paneScrim` keeps that edge tracking the rail as it
  // collapses/expands, and the RailCatcher below makes the rail inert while the dialog is open.
  return createPortal(
    <div
      className={[
        paneScrim.paneScrim,
        besideChat ? paneScrim.besideChat : undefined,
        styles.overlay,
        fillsSmall ? styles.fullScreenScrim : undefined,
        sheet ? styles.sheetOverlay : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      // Scrim click closes — but only a press that both starts AND ends on the scrim, so a text selection
      // that drifts out of the card doesn't dismiss it.
      onMouseDown={(e) => {
        if (dismissOnScrimClick && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <RailCatcher onClose={dismissOnScrimClick ? onClose : noop} zIndex={1000} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-theme={cardTheme}
        className={[
          styles.card,
          cardClassName,
          fillsSmall ? styles.fullScreenCard : undefined,
          // The presenter's page is `surface/page-background`, not the raised card surface — the node's
          // own first row, and what gives the leading ✕'s white circle something to sit ON. See
          // `.presenterCard`.
          presenterHeader ? styles.presenterCard : undefined,
          scrollBody ? styles.cardScroll : undefined,
          maxHeightPx ? styles.cardCap : undefined,
          sheet ? styles.sheetCard : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          {
            '--modal-w': `${modalWidth}px`,
            ...(maxHeightPx ? { '--modal-max-h': `${maxHeightPx}px` } : {}),
          } as CSSProperties
        }
      >
        {/* The grab handle. Decorative here — the sheet is dismissed by its own ✕, by Escape and by a
            scrim press — but it is what says "this is a sheet" before anything else is read. */}
        {sheet && <span className={styles.sheetHandle} aria-hidden />}
        {fullScreenBack && (
          <IconButton aria-label={t('common.back')} variant="text" size="medium" onClick={onClose} className={styles.back}>
            <ChevronLeft size={24} aria-hidden className="dir-flip" />
          </IconButton>
        )}
        {!presenterHeader && closeButton}
        {hasHeader && (
          <div className={headerClasses}>
            {/* On the presenter the ✕ is the row's LEADING item, ahead of the title block — in flow, so
                the 16 beside it and the 12 to the trailing cluster are real gaps rather than a padding
                guess against an absolutely-positioned mark. */}
            {presenterHeader && closeButton}
            <div className={styles.headerContent}>
              {backAction && (
                <TextButton
                  size="tiny"
                  className={styles.backToRow}
                  startIcon={<ChevronLeft size={16} aria-hidden className="dir-flip" />}
                  onClick={backAction.onBack}
                >
                  {backAction.label}
                </TextButton>
              )}
              {(title || titleInfo) && (
                <div className={styles.titleRow} id={labelledBy}>
                  {title && <span className={styles.title}>{title}</span>}
                  {titleInfo && <Info className={styles.titleInfo} size={18} aria-label={titleInfo} />}
                </div>
              )}
              {description && <div className={styles.description}>{description}</div>}
            </div>
            {/* The trailing cluster. The shell owns its box (gap 8, no shrink) so every caller's row of
                header controls is spaced and aligned the same way — see the `headerActions` doc. */}
            {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
          </div>
        )}
        {hasHeader && headerSeparator && <div className={styles.separator} />}
        {scrollBody ? (
          <ScrollArea className={[...contentClasses, styles.scrollBody].join(' ')}>{children}</ScrollArea>
        ) : (
          <div className={contentClasses.join(' ')}>{children}</div>
        )}
        {footer && (
          <>
            {footerSeparator && <div className={styles.separator} />}
            <div className={footerClasses}>{footer}</div>
          </>
        )}
        {footnote && <div className={styles.footnote}>{footnote}</div>}
      </div>
    </div>,
    document.body,
  );
}
